import { useState } from "react";
import {
    Box, Typography, Grid, Avatar, Chip, useMediaQuery, Tabs, Tab
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import { AiFillBulb } from "react-icons/ai";

const PageWrapper = styled(Box)(() => ({
    minHeight: "100vh",
    background: "linear-gradient(160deg, #03020a 0%, #060c18 50%, #03050f 100%)",
    color: "#fff",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    paddingBottom: "60px",
    paddingTop: "32px",
}));

const SectionCard = styled(Box)(() => ({
    background: "rgba(26,14,52,0.95)",
    border: "1px solid rgba(218,165,32,0.25)",
    borderRadius: "18px",
    padding: "32px",
    marginBottom: "28px",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: "10%", right: "10%", height: "1px",
        background: "linear-gradient(to right,transparent,rgba(218,165,32,0.3),transparent)",
    },
}));

const MobileDataCard = styled(Box)(() => ({
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(218,165,32,0.15)",
    borderRadius: "12px",
    padding: "14px 16px",
    marginBottom: "10px",
}));

const CodeBlock = styled(Box)(() => ({
    background: "rgba(6,2,16,0.98)",
    border: "1px solid rgba(218,165,32,0.2)",
    borderRadius: "12px",
    padding: "20px 24px",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    fontSize: "0.82rem",
    lineHeight: 1.9,
    overflowX: "auto",
    whiteSpace: "pre",
    color: "#e6edf3",
    position: "relative",
    "&::before": {
        content: '"C++"',
        position: "absolute",
        top: "8px", right: "14px",
        fontSize: "9px",
        color: "rgba(218,165,32,0.5)",
        letterSpacing: "2px",
        fontWeight: 700,
    },
}));

const TableHeader = ({ columns, cols }) => (
    <Box sx={{
        display: "grid",
        gridTemplateColumns: cols || `repeat(${columns.length},1fr)`,
        gap: "12px", padding: "12px 16px",
        bgcolor: "rgba(218,165,32,0.1)", color: "goldenrod",
        fontWeight: "bold", fontSize: "0.78rem",
        letterSpacing: "0.06em", textTransform: "uppercase",
        borderBottom: "1px solid rgba(218,165,32,0.15)",
    }}>
        {columns.map(c => <div key={c}>{c}</div>)}
    </Box>
);

const SectionHeading = ({ icon, title, subtitle }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mt: 4, mb: subtitle ? 1 : 2 }}>
        <Avatar sx={{ bgcolor: "goldenrod", color: "#100720", width: 38, height: 38, flexShrink: 0, mt: 0.3 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.45)", mt: 0.3 }}>{subtitle}</Typography>}
        </Box>
    </Box>
);

const ConceptCard = ({ title, color = "goldenrod", children }) => (
    <Box sx={{
        bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${color}33`,
        borderLeft: `3px solid ${color}`, borderRadius: "12px",
        p: "16px 18px", mb: 2,
    }}>
        {title && <Typography sx={{ fontWeight: 700, color, fontSize: "0.82rem", mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</Typography>}
        {children}
    </Box>
);

const StyledTabs = ({ value, onChange, tabs }) => (
    <Tabs value={value} onChange={onChange} variant="scrollable" scrollButtons="auto"
          sx={{
              mb: 2, borderBottom: "1px solid rgba(255,255,255,0.1)",
              "& .MuiTab-root": { fontSize: "0.8rem", fontWeight: 600, color: "rgba(255,255,255,0.45)", textTransform: "none", minHeight: 40 },
              "& .Mui-selected": { color: "goldenrod !important" },
              "& .MuiTabs-indicator": { bgcolor: "goldenrod" },
          }}>
        {tabs.map((t, i) => <Tab key={i} label={t} />)}
    </Tabs>
);

const PillChip = ({ label, color = "goldenrod" }) => (
    <Chip label={label} size="small" sx={{
        bgcolor: `${color}18`, color, fontWeight: 700, fontSize: "0.72rem",
        height: 22, border: `1px solid ${color}44`, "& .MuiChip-label": { px: 1 },
    }} />
);
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// COLLECTION OVERVIEW DATA
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const collectionOverview = [
    { name: "vector<T>",         cat: "Sequence", color: "#58a6ff", icon: "📦", access: "O(1)",     insert: "O(1)*",   search: "O(n)",    ordered: "✅ Insertion", use: "Default list / dynamic array" },
    { name: "deque<T>",          cat: "Sequence", color: "#67e8f9", icon: "↔️", access: "O(1)",     insert: "O(1)*",   search: "O(n)",    ordered: "✅ Insertion", use: "Fast front + back insert" },
    { name: "list<T>",           cat: "Sequence", color: "#a5b4fc", icon: "🔗", access: "O(n)",     insert: "O(1)†",  search: "O(n)",    ordered: "✅ Insertion", use: "Frequent mid-list insert/erase" },
    { name: "forward_list<T>",   cat: "Sequence", color: "#c4b5fd", icon: "➡️", access: "O(n)",     insert: "O(1)†",  search: "O(n)",    ordered: "✅ Insertion", use: "Memory-constrained singly linked" },
    { name: "set<T>",            cat: "Associative", color: "#7ee787", icon: "🌳", access: "O(log n)", insert: "O(log n)", search: "O(log n)", ordered: "✅ Sorted",    use: "Unique sorted elements" },
    { name: "multiset<T>",       cat: "Associative", color: "#86efac", icon: "🌲", access: "O(log n)", insert: "O(log n)", search: "O(log n)", ordered: "✅ Sorted",    use: "Sorted with duplicates" },
    { name: "map<K,V>",          cat: "Associative", color: "#fbbf24", icon: "🗺️", access: "O(log n)", insert: "O(log n)", search: "O(log n)", ordered: "✅ Key sorted", use: "Sorted key→value mapping" },
    { name: "multimap<K,V>",     cat: "Associative", color: "#fde68a", icon: "🗺️", access: "O(log n)", insert: "O(log n)", search: "O(log n)", ordered: "✅ Key sorted", use: "Sorted map with dup keys" },
    { name: "unordered_set<T>",  cat: "Unordered",   color: "#f0883e", icon: "⚡", access: "O(1)*",    insert: "O(1)*",    search: "O(1)*",    ordered: "❌ None",      use: "Fast unique membership test" },
    { name: "unordered_map<K,V>",cat: "Unordered",   color: "#fb923c", icon: "⚡", access: "O(1)*",    insert: "O(1)*",    search: "O(1)*",    ordered: "❌ None",      use: "Fast key→value lookup" },
    { name: "stack<T>",          cat: "Adaptor",     color: "#f87171", icon: "📚", access: "top only", insert: "O(1)",    search: "❌ None",  ordered: "LIFO",         use: "LIFO — undo, DFS, parsing" },
    { name: "queue<T>",          cat: "Adaptor",     color: "#fca5a5", icon: "🚶", access: "front/back",insert:"O(1)",    search: "❌ None",  ordered: "FIFO",         use: "FIFO — BFS, task queues" },
    { name: "priority_queue<T>", cat: "Adaptor",     color: "#e879f9", icon: "🔝", access: "top only", insert: "O(log n)", search: "❌ None", ordered: "Heap order",   use: "Always access largest first" },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 1: vector
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const vectorTabs = [
    {
        label: "Core Operations",
        code: `#include <vector>
#include <algorithm>
#include <numeric>
#include <iostream>

std::vector<int> v = {3, 1, 4, 1, 5, 9, 2, 6};

// ── Access ─────────────────────────────────────────────────────
v[0];         // 3   — no bounds check (fast)
v.at(0);      // 3   — bounds-checked, throws out_of_range
v.front();    // 3   — first element
v.back();     // 6   — last element
v.data();     // raw pointer to first element (for C APIs)

// ── Modify ─────────────────────────────────────────────────────
v.push_back(7);                    // append — O(1) amortised
v.emplace_back(8);                 // construct in-place — no copy
v.pop_back();                      // remove last — O(1), no return
v.insert(v.begin() + 2, 99);       // insert at index 2 — O(n)
v.erase(v.begin());                // remove first — O(n)
v.erase(v.begin()+1, v.begin()+3); // erase range [1,3) — O(n)
v.clear();                         // remove all, size→0

// ── Size & capacity ────────────────────────────────────────────
v.size();          // elements currently stored
v.capacity();      // allocated space (may be > size)
v.empty();         // true if size == 0
v.reserve(100);    // pre-allocate — avoids reallocations
v.resize(10, 0);   // resize, fill new slots with 0
v.shrink_to_fit(); // release excess capacity

// ── Algorithms ─────────────────────────────────────────────────
std::sort(v.begin(), v.end());
std::reverse(v.begin(), v.end());
int sum = std::accumulate(v.begin(), v.end(), 0);
auto it = std::find(v.begin(), v.end(), 5);
bool has = std::binary_search(v.begin(), v.end(), 5);  // needs sorted!`,
    },
    {
        label: "Erase-Remove & Partition",
        code: `#include <vector>
#include <algorithm>

std::vector<int> v = {1, 2, 3, 2, 4, 2, 5, 6};

// ── Erase-Remove idiom — remove ALL matching values ────────────
v.erase(std::remove(v.begin(), v.end(), 2), v.end());
// v = {1, 3, 4, 5, 6}

// ── Erase-Remove-If — remove by predicate ─────────────────────
v.erase(
    std::remove_if(v.begin(), v.end(),
                   [](int x){ return x % 2 == 0; }),
    v.end()
);  // removes all even numbers

// ── C++20 shorthand ────────────────────────────────────────────
std::vector<int> w = {1, 2, 2, 3, 2};
std::erase(w, 2);                              // remove all 2s
std::erase_if(w, [](int x){ return x > 3; }); // remove if > 3

// ── std::partition — split into two groups ─────────────────────
std::vector<int> nums = {1,2,3,4,5,6,7,8};
auto mid = std::partition(nums.begin(), nums.end(),
    [](int x){ return x % 2 == 0; });  // evens first, then odds
// nums = {2,4,6,8, 1,3,5,7}  (order within groups not preserved)
// mid points to first odd

// ── std::stable_partition — preserves relative order ──────────
std::stable_partition(nums.begin(), nums.end(),
    [](int x){ return x % 2 == 0; });

// ── Remove duplicates (sorted vector) ─────────────────────────
std::sort(v.begin(), v.end());
v.erase(std::unique(v.begin(), v.end()), v.end());`,
    },
    {
        label: "2D Vector",
        code: `#include <vector>
#include <iostream>

// ── Dynamic 2D grid ────────────────────────────────────────────
int rows = 3, cols = 4;
std::vector<std::vector<int>> grid(rows, std::vector<int>(cols, 0));

grid[1][2] = 99;                // access like 2D array

// ── Iterate ────────────────────────────────────────────────────
for (const auto& row : grid) {
    for (int val : row)
        std::cout << val << '\t';
    std::cout << '\n';
}

// ── Add row ────────────────────────────────────────────────────
grid.push_back(std::vector<int>(cols, -1));   // new row of -1s

// ── Jagged 2D (rows of different sizes) ───────────────────────
std::vector<std::vector<int>> tri;
for (int i = 1; i <= 5; ++i)
    tri.emplace_back(i, i);   // row i: {i, i, ..., i} of length i

// ── Transpose ─────────────────────────────────────────────────
auto transpose = [](const std::vector<std::vector<int>>& m) {
    int R = m.size(), C = m[0].size();
    std::vector<std::vector<int>> t(C, std::vector<int>(R));
    for (int r = 0; r < R; ++r)
        for (int c = 0; c < C; ++c)
            t[c][r] = m[r][c];
    return t;
};`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 2: deque, list, forward_list
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const seqTabs = [
    {
        label: "deque",
        code: `#include <deque>
#include <iostream>

// ── std::deque — double-ended queue ───────────────────────────
// Like vector but O(1) push/pop at BOTH front AND back
// Stored as chunks (not fully contiguous) — slightly slower random access

std::deque<int> dq = {3, 1, 4, 1, 5};

// ── O(1) operations at BOTH ends ──────────────────────────────
dq.push_front(0);         // {0, 3, 1, 4, 1, 5}  ← fast!
dq.push_back(9);          // {0, 3, 1, 4, 1, 5, 9}
dq.pop_front();           // remove first — O(1) unlike vector!
dq.pop_back();            // remove last

// ── Access ─────────────────────────────────────────────────────
dq[0];        // random access — O(1) but slightly slower than vector
dq.front();   // first element
dq.back();    // last element
dq.at(2);     // bounds-checked

// ── Insert / Erase in middle — O(n) ──────────────────────────
dq.insert(dq.begin() + 2, 99);
dq.erase(dq.begin());

// ── Classic use: sliding window maximum ───────────────────────
// Maintain a monotonic decreasing deque of indices:
std::vector<int> maxSliding(std::vector<int>& nums, int k) {
    std::deque<int> dq;             // stores INDICES
    std::vector<int> result;
    for (int i = 0; i < (int)nums.size(); ++i) {
        while (!dq.empty() && dq.front() < i - k + 1)
            dq.pop_front();         // remove out-of-window indices
        while (!dq.empty() && nums[dq.back()] < nums[i])
            dq.pop_back();          // remove smaller elements
        dq.push_back(i);
        if (i >= k - 1) result.push_back(nums[dq.front()]);
    }
    return result;
}`,
    },
    {
        label: "list (doubly linked)",
        code: `#include <list>
#include <algorithm>

// ── std::list — doubly linked list ────────────────────────────
// O(1) insert/erase ANYWHERE with an iterator
// No random access — must traverse to find elements
// Use when: frequent insertions/deletions in middle of sequence

std::list<int> lst = {3, 1, 4, 1, 5, 9, 2, 6};

// ── Insert / Erase — O(1) with iterator ───────────────────────
auto it = std::find(lst.begin(), lst.end(), 4);
lst.insert(it, 99);        // insert 99 BEFORE the 4 — O(1)!
lst.erase(it);             // erase the 4 — O(1)!

// ── Front / Back ───────────────────────────────────────────────
lst.push_front(0);
lst.push_back(10);
lst.pop_front();
lst.pop_back();

// ── List-specific algorithms ───────────────────────────────────
lst.sort();                // in-place merge sort — O(n log n)
lst.reverse();             // reverse in-place — O(n)
lst.unique();              // remove consecutive duplicates (sort first!)
lst.remove(1);             // remove ALL elements equal to 1 — O(n)
lst.remove_if([](int x){ return x > 5; }); // remove by predicate

// ── splice — O(1) move elements between lists ──────────────────
std::list<int> lst2 = {10, 20, 30};
lst.splice(lst.begin(), lst2);  // move ALL of lst2 into lst start
// lst2 is now empty; elements transferred in O(1)!

std::list<int> lst3 = {7, 8, 9};
auto it2 = std::next(lst3.begin());
lst.splice(lst.end(), lst3, it2);  // move single element 8`,
    },
    {
        label: "forward_list",
        code: `#include <forward_list>
#include <algorithm>

// ── std::forward_list — singly linked list ─────────────────────
// Memory: one pointer per node vs two for std::list
// Trade-off: no reverse iteration, no push_back, no size()
// Use when: memory is extremely tight and you only iterate forward

std::forward_list<int> fl = {3, 1, 4, 1, 5};

// ── Insert / Erase — uses "after" convention ──────────────────
// forward_list provides insert_after / erase_after (not insert/erase!)
fl.push_front(0);                     // {0, 3, 1, 4, 1, 5}
// fl.push_back(...);                 // ❌ NOT available!

auto it = fl.begin();                 // points to 0
fl.insert_after(it, 99);              // {0, 99, 3, 1, 4, 1, 5}
fl.erase_after(it);                   // erase element AFTER it

// ── before_begin() — to insert at very front ──────────────────
fl.insert_after(fl.before_begin(), -1);  // {-1, 0, 3, ...}

// ── Algorithms ─────────────────────────────────────────────────
fl.sort();
fl.reverse();
fl.unique();
fl.remove(1);
fl.remove_if([](int x){ return x < 0; });

// ── No size() — count manually ────────────────────────────────
int count = std::distance(fl.begin(), fl.end());  // O(n)!

// ── When to prefer list vs forward_list ───────────────────────
//  forward_list:  read-only or forward-only traversal, tight memory
//  list:          need both directions, splice, push_back, size()`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 3: set, multiset
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const setTabs = [
    {
        label: "set",
        code: `#include <set>
#include <algorithm>
#include <vector>

// ── std::set<T> — unique sorted elements ──────────────────────
// Internally: self-balancing BST (red-black tree)
// All operations: O(log n)  |  Always sorted

std::set<int> s = {5, 1, 3, 2, 4, 1};  // duplicates discarded!
// s = {1, 2, 3, 4, 5}  ← automatically sorted!

// ── Insert & Remove ────────────────────────────────────────────
auto [it, inserted] = s.insert(6);   // C++17 structured binding
// it = iterator to 6,  inserted = true
s.insert(3);  // duplicate ignored — inserted = false
s.emplace(7); // construct in-place
s.erase(3);   // remove value 3
s.erase(s.begin()); // remove smallest element

// ── Lookup ─────────────────────────────────────────────────────
s.count(4);   // 0 or 1 (set has no duplicates)
s.find(4) != s.end();  // true if found

// ── Range queries — the power of set ─────────────────────────
auto lb = s.lower_bound(3);   // iterator to first element >= 3
auto ub = s.upper_bound(6);   // iterator to first element >  6
// All elements in [3,6]: iterate from lb to ub

// count elements in range [lo, hi]:
int countInRange(const std::set<int>& s, int lo, int hi) {
    return std::distance(s.lower_bound(lo), s.upper_bound(hi));
}

// ── Set operations (using STL algorithms) ─────────────────────
std::set<int> a{1,2,3,4}, b{3,4,5,6};
std::vector<int> intersection, diff, unionSet;
std::set_intersection(a.begin(),a.end(), b.begin(),b.end(),
    std::back_inserter(intersection));   // {3,4}
std::set_difference(a.begin(),a.end(), b.begin(),b.end(),
    std::back_inserter(diff));           // {1,2}`,
    },
    {
        label: "multiset",
        code: `#include <set>
#include <iostream>

// ── std::multiset<T> — sorted, ALLOWS duplicates ──────────────
// Same as set but count() can return > 1

std::multiset<int> ms = {5, 1, 3, 1, 5, 3, 2};
// ms = {1, 1, 2, 3, 3, 5, 5}  ← sorted, duplicates kept

ms.insert(3);        // now has three 3s: {1,1,2,3,3,3,5,5}
ms.count(3);         // 3 — how many 3s

// ── Erase: be careful! ─────────────────────────────────────────
ms.erase(1);         // removes ALL copies of 1! → {2,3,3,3,5,5}
// To remove just ONE copy:
auto it = ms.find(3);
if (it != ms.end()) ms.erase(it);  // removes exactly one 3

// ── equal_range — find ALL elements with a given value ─────────
auto [lo, hi] = ms.equal_range(5);
for (auto it = lo; it != hi; ++it)
    std::cout << *it << ' ';  // all 5s

// ── Classic use: frequency counter with ordering ──────────────
// Count character frequencies in sorted order:
std::string text = "programming";
std::multiset<char> chars(text.begin(), text.end());
// chars: sorted chars with duplicates — iterate to see all

// ── Custom comparator — sort descending ───────────────────────
std::set<int, std::greater<int>> desc = {1,2,3,4,5};
// desc = {5,4,3,2,1}  ← largest first!

std::multiset<std::string, std::greater<std::string>> words;
words.insert({"banana","apple","cherry","apple"});
// Sorted: {"cherry","banana","apple","apple"}`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 4: map, multimap
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const mapTabs = [
    {
        label: "map",
        code: `#include <map>
#include <string>
#include <iostream>

// ── std::map<K,V> — sorted key→value mapping ──────────────────
// Keys unique, sorted, BST-based — O(log n) all operations

std::map<std::string, int> scores;

// ── Insert ─────────────────────────────────────────────────────
scores["Alice"] = 95;                  // operator[] — creates if absent!
scores.insert({"Bob", 88});
scores.emplace("Carol", 92);           // construct in-place
scores.insert_or_assign("Alice", 99);  // update or insert (C++17)
scores.try_emplace("Dave", 75);        // insert only if key absent (C++17)

// ── Access ─────────────────────────────────────────────────────
scores["Alice"];        // 99 — creates default (0) if key absent!
scores.at("Alice");     // 99 — throws out_of_range if absent ← SAFER

// ── Lookup (safe) ──────────────────────────────────────────────
auto it = scores.find("Bob");
if (it != scores.end())
    std::cout << it->first << ": " << it->second << '\\n';

scores.count("Carol");   // 0 or 1 (map has unique keys)
scores.contains("Dave"); // true (C++20)

// ── Erase ──────────────────────────────────────────────────────
scores.erase("Carol");
scores.erase(scores.begin());   // remove first (smallest key)

// ── Iterate in KEY ORDER ───────────────────────────────────────
for (const auto& [name, score] : scores)   // structured binding C++17
    std::cout << name << " → " << score << '\\n';
// Output in alphabetical order by name

// ── Range queries — works like set ────────────────────────────
auto lo = scores.lower_bound("B");
auto hi = scores.upper_bound("D");
for (auto it = lo; it != hi; ++it)
    std::cout << it->first;  // all names starting with B,C,D`,
    },
    {
        label: "map patterns",
        code: `#include <map>
#include <string>
#include <vector>
#include <algorithm>

// ── Pattern 1: frequency counter ──────────────────────────────
std::map<std::string, int> wordCount(const std::vector<std::string>& words) {
    std::map<std::string,int> freq;
    for (const auto& w : words)
        ++freq[w];   // operator[] default-initialises int to 0
    return freq;
}

// ── Pattern 2: group by / bucket ──────────────────────────────
// Group strings by their length
std::map<int, std::vector<std::string>> groupByLength(
        const std::vector<std::string>& words) {
    std::map<int, std::vector<std::string>> groups;
    for (const auto& w : words)
        groups[w.size()].push_back(w);  // auto-creates vector if key absent
    return groups;
}

// ── Pattern 3: memoisation / cache ────────────────────────────
std::map<int, long long> memo;
long long fib(int n) {
    if (n <= 1) return n;
    if (memo.count(n)) return memo[n];   // cache hit
    return memo[n] = fib(n-1) + fib(n-2); // store result
}

// ── Pattern 4: inverse map ────────────────────────────────────
std::map<std::string,int> scores = {{"Alice",95},{"Bob",88},{"Carol",92}};
std::map<int,std::string> byScore;   // value → key
for (const auto& [name,score] : scores)
    byScore[score] = name;
// Now lookup by score: byScore[95] → "Alice"`,
    },
    {
        label: "multimap",
        code: `#include <map>
#include <string>
#include <iostream>

// ── std::multimap<K,V> — sorted map, DUPLICATE KEYS allowed ───

std::multimap<std::string, int> grades;
grades.insert({"Alice", 92});
grades.insert({"Alice", 88});   // Alice can have multiple scores!
grades.insert({"Bob",   75});
grades.insert({"Alice", 95});

grades.count("Alice");  // 3 — three entries with key "Alice"

// ── Get all values for a key ──────────────────────────────────
auto [lo, hi] = grades.equal_range("Alice");
for (auto it = lo; it != hi; ++it)
    std::cout << it->first << ": " << it->second << '\\n';
// Alice: 92   Alice: 88   Alice: 95

// ── operator[] NOT available on multimap ──────────────────────
// grades["Alice"];   // ❌ COMPILE ERROR — ambiguous which value?
// Use insert() and find()/equal_range() instead

// ── Erase all for one key ─────────────────────────────────────
grades.erase("Alice");   // removes ALL Alice entries at once

// ── Common use: one-to-many relationship ──────────────────────
// Student → courses (one student, multiple courses)
std::multimap<std::string, std::string> enrollment;
enrollment.insert({"Alice", "Math"});
enrollment.insert({"Alice", "Physics"});
enrollment.insert({"Bob",   "Math"});

// All courses for Alice:
auto [a, b] = enrollment.equal_range("Alice");
for (auto it = a; it != b; ++it)
    std::cout << it->second << '\\n';  // Math  Physics`,
    },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 5: unordered_set, unordered_map
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const hashTabs = [
    {
        label: "unordered_set",
        code: `#include <unordered_set>
#include <string>
#include <iostream>

// ── std::unordered_set<T> — hash-based, O(1) average ──────────
// No ordering. Fast membership test. No duplicates.
// Internally: hash table with buckets

std::unordered_set<int> us = {5, 1, 3, 2, 4};
// Order of iteration is UNDEFINED — not necessarily sorted!

// ── Insert & Remove — O(1) average ────────────────────────────
us.insert(6);             // O(1) average
us.emplace(7);
us.erase(3);              // O(1) average
us.clear();               // remove all

// ── Lookup — O(1) average ──────────────────────────────────────
us.count(4);              // 0 or 1 (no duplicates)
us.find(4) != us.end();   // true if found
us.contains(4);           // C++20 — cleaner way

// ── Performance tuning ─────────────────────────────────────────
us.reserve(1000);         // pre-allocate 1000 buckets — avoid rehashing!
us.max_load_factor(0.7);  // rehash when 70% full (default 1.0)
us.bucket_count();        // current number of buckets
us.load_factor();         // size / bucket_count

// ── Classic use: O(n) duplicate detection ─────────────────────
bool hasDuplicate(const std::vector<int>& v) {
    std::unordered_set<int> seen;
    for (int x : v) {
        if (!seen.insert(x).second) return true;  // insert returns {it, bool}
    }
    return false;
}

// ── Two Sum with unordered_set ────────────────────────────────
std::vector<int> twoSum(std::vector<int>& nums, int target) {
    std::unordered_map<int,int> idx;
    for (int i=0; i<(int)nums.size(); ++i) {
        int complement = target - nums[i];
        if (idx.count(complement)) return {idx[complement], i};
        idx[nums[i]] = i;
    }
    return {};
}`,
    },
    {
        label: "unordered_map",
        code: `#include <unordered_map>
#include <string>
#include <vector>

// ── std::unordered_map<K,V> — hash table map ──────────────────
// O(1) average for all operations vs O(log n) for std::map
// Trade-off: no ordering, keys must be hashable

std::unordered_map<std::string, int> ages;

// ── Insert ─────────────────────────────────────────────────────
ages["Alice"]  = 30;             // operator[] — creates default if absent
ages.insert({"Bob", 25});
ages.emplace("Carol", 28);
ages.insert_or_assign("Alice", 31);  // update or insert (C++17)
ages.try_emplace("Dave", 22);        // insert only if absent (C++17)

// ── Lookup ─────────────────────────────────────────────────────
ages["Alice"];                // 31 — creates default entry if missing!
ages.at("Alice");             // 31 — throws out_of_range if absent
ages.count("Bob");            // 0 or 1
ages.contains("Carol");       // C++20

auto it = ages.find("Bob");
if (it != ages.end())
    std::cout << it->first << ": " << it->second;

// ── Erase ──────────────────────────────────────────────────────
ages.erase("Carol");

// ── Iterate (NO guaranteed order) ──────────────────────────────
for (const auto& [name, age] : ages)
    std::cout << name << " → " << age << '\\n';

// ── Custom hash for user-defined types ────────────────────────
struct Point { int x, y; };
struct PointHash {
    size_t operator()(const Point& p) const {
        return std::hash<int>()(p.x) ^ (std::hash<int>()(p.y) << 1);
    }
};
struct PointEq {
    bool operator()(const Point& a, const Point& b) const {
        return a.x == b.x && a.y == b.y;
    }
};
std::unordered_map<Point, std::string, PointHash, PointEq> labels;`,
    },
    {
        label: "map vs unordered_map",
        code: `// ── When to use which ─────────────────────────────────────────
//
//  std::map<K,V>                  std::unordered_map<K,V>
//  ─────────────────────────────  ──────────────────────────────
//  O(log n) all ops               O(1) average all ops
//  Always sorted by key           No order guaranteed
//  Range queries (lower_bound)    No range queries
//  Key needs operator<            Key needs hash + equality
//  Predictable performance        Worst case O(n) (hash collision)
//  Less memory                    More memory (buckets + load)
//  Cache-unfriendly (tree nodes)  More cache-friendly (buckets)
//
//  Use std::map when:
//    ✅ You need iteration in key order
//    ✅ You need range queries (lower/upper_bound)
//    ✅ Keys can't be hashed (complex objects)
//    ✅ Performance must be predictable (real-time systems)
//
//  Use unordered_map when:
//    ✅ You only need lookup by exact key
//    ✅ Performance matters and n is large
//    ✅ Keys are ints, strings (built-in hash)
//    ✅ No ordering needed

// ── reserve() is critical for unordered containers ────────────
std::unordered_map<int,int> freq;
freq.reserve(10000);   // avoids rehashing as map grows — BIG speedup!

// ── Benchmark guideline ────────────────────────────────────────
// n=1000:    map vs unordered_map → similar (overhead dominates)
// n=100000:  unordered_map ~5-10x faster for lookup-heavy workloads
// n=1000000: unordered_map can be 20x faster`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 6: stack, queue, priority_queue
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const adaptorTabs = [
    {
        label: "stack",
        code: `#include <stack>
#include <string>
#include <iostream>

// ── std::stack<T> — LIFO container adaptor ────────────────────
// Default underlying container: std::deque
// Interface: push, pop, top, empty, size — no iterators!

std::stack<int> stk;

// ── Core operations — all O(1) ────────────────────────────────
stk.push(1);          // push onto top
stk.push(2);
stk.push(3);
stk.emplace(4);       // construct in-place on top

stk.top();            // 4 — peek at top WITHOUT removing
stk.pop();            // remove top (returns void — no value!)
stk.size();           // 3
stk.empty();          // false

// ── Classic: valid parentheses ────────────────────────────────
bool isValid(const std::string& s) {
    std::stack<char> st;
    for (char c : s) {
        if (c=='(' || c=='[' || c=='{') { st.push(c); continue; }
        if (st.empty()) return false;
        char top = st.top(); st.pop();
        if ((c==')' && top!='(') ||
            (c==']' && top!='[') ||
            (c=='}' && top!='{')) return false;
    }
    return st.empty();
}

// ── Classic: evaluate postfix expression ──────────────────────
int evalPostfix(const std::string& expr) {
    std::stack<int> st;
    for (char c : expr) {
        if (isdigit(c)) { st.push(c - '0'); continue; }
        int b = st.top(); st.pop();
        int a = st.top(); st.pop();
        if (c=='+') st.push(a+b);
        else if (c=='-') st.push(a-b);
        else if (c=='*') st.push(a*b);
        else if (c=='/') st.push(a/b);
    }
    return st.top();  // "32+5*" → (3+2)*5 = 25
}`,
    },
    {
        label: "queue",
        code: `#include <queue>
#include <vector>
#include <iostream>

// ── std::queue<T> — FIFO container adaptor ────────────────────
// Default underlying container: std::deque
// Interface: push, pop, front, back, empty, size

std::queue<int> q;

// ── Core operations — all O(1) ────────────────────────────────
q.push(1);    q.push(2);    q.push(3);
q.emplace(4);

q.front();    // 1 — first in
q.back();     // 4 — last pushed
q.pop();      // remove front (returns void!)
q.size();     // 3
q.empty();    // false

// ── Classic: BFS shortest path ────────────────────────────────
#include <unordered_map>
int bfsShortestPath(
        std::unordered_map<int,std::vector<int>>& adj,
        int start, int end) {
    std::queue<int> q;
    std::unordered_map<int,int> dist;
    q.push(start);
    dist[start] = 0;
    while (!q.empty()) {
        int node = q.front(); q.pop();
        if (node == end) return dist[node];
        for (int nbr : adj[node]) {
            if (!dist.count(nbr)) {
                dist[nbr] = dist[node] + 1;
                q.push(nbr);
            }
        }
    }
    return -1;  // not reachable
}

// ── Task queue / producer-consumer ────────────────────────────
std::queue<std::string> taskQueue;
taskQueue.push("task1");
taskQueue.push("task2");
while (!taskQueue.empty()) {
    std::string task = taskQueue.front();
    taskQueue.pop();
    // process task...
}`,
    },
    {
        label: "priority_queue",
        code: `#include <queue>
#include <vector>
#include <iostream>

// ── std::priority_queue<T> — max-heap by default ──────────────
// push: O(log n)  |  pop: O(log n)  |  top: O(1)
// Always gives you the LARGEST element first

std::priority_queue<int> pq;  // MAX-heap
pq.push(5); pq.push(1); pq.push(8); pq.push(3);
pq.top();   // 8 — largest
pq.pop();   // remove 8
pq.top();   // 5 — next largest

// ── MIN-heap — use std::greater<T> ────────────────────────────
std::priority_queue<int,
    std::vector<int>,
    std::greater<int>> minPQ;  // SMALLEST first!
minPQ.push(5); minPQ.push(1); minPQ.push(3);
minPQ.top();   // 1 — smallest

// ── Custom comparator (e.g., sort pairs by second element) ────
using P = std::pair<int,int>;
auto cmp = [](const P& a, const P& b){ return a.second < b.second; };
std::priority_queue<P, std::vector<P>, decltype(cmp)> pq2(cmp);
pq2.push({1,10}); pq2.push({2,5}); pq2.push({3,8});
pq2.top();  // {1,10} — largest second element

// ── Classic: K largest elements ───────────────────────────────
std::vector<int> kLargest(std::vector<int>& nums, int k) {
    // Min-heap of size k — only keep k largest
    std::priority_queue<int, std::vector<int>, std::greater<int>> minH;
    for (int x : nums) {
        minH.push(x);
        if ((int)minH.size() > k) minH.pop();  // remove smallest
    }
    std::vector<int> result;
    while (!minH.empty()) { result.push_back(minH.top()); minH.pop(); }
    return result;
}

// ── Classic: Dijkstra with priority_queue ─────────────────────
// Use pair<int,int> = {distance, node} in a min-heap
std::priority_queue<P, std::vector<P>, std::greater<P>> dijkstraPQ;
dijkstraPQ.push({0, startNode});`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CHEATSHEET DATA
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const decisionGuide = [
    { need: "Dynamic list, random access",     best: "vector<T>",           why: "Cache-friendly, O(1) access, default choice" },
    { need: "Fast insert/remove at BOTH ends", best: "deque<T>",            why: "O(1) push_front and push_back" },
    { need: "Fast insert/remove in MIDDLE",    best: "list<T>",             why: "O(1) with iterator, O(1) splice" },
    { need: "Unique sorted elements",          best: "set<T>",              why: "Always sorted, O(log n) range queries" },
    { need: "Sorted elements WITH duplicates", best: "multiset<T>",         why: "Like set but count(x) can be > 1" },
    { need: "Sorted key→value map",            best: "map<K,V>",            why: "Sorted, range queries, O(log n)" },
    { need: "One key → many values",           best: "multimap<K,V>",       why: "Use equal_range to get all values" },
    { need: "Fast unique membership",          best: "unordered_set<T>",    why: "O(1) average lookup — hash table" },
    { need: "Fast key→value lookup",           best: "unordered_map<K,V>",  why: "O(1) average — best for large n" },
    { need: "LIFO / undo / recursion stack",   best: "stack<T>",            why: "Only push/pop/top — enforces LIFO" },
    { need: "FIFO / task queue / BFS",         best: "queue<T>",            why: "Only push/pop/front — enforces FIFO" },
    { need: "Always process largest/smallest", best: "priority_queue<T>",   why: "Heap: O(log n) push, O(1) top" },
];

const cheatBlocks = [
    {
        title: "Sequence Containers", color: "#58a6ff",
        items: [
            "✅ vector: default choice for any sequence",
            "✅ reserve(n) if final size is known — avoids reallocations",
            "✅ emplace_back() over push_back() — avoids copy",
            "✅ deque: when you need O(1) push_front AND push_back",
            "✅ list: O(1) splice is its superpower",
            "❌ Don't erase in a loop without using returned iterator",
            "❌ Don't use forward_list unless memory is critically tight",
        ],
    },
    {
        title: "Associative Containers", color: "#7ee787",
        items: [
            "✅ map/set: use when sorted order matters",
            "✅ unordered_map/set: use for O(1) lookup at large n",
            "✅ reserve() unordered containers to avoid rehashing",
            "✅ Use find() not operator[] to check existence safely",
            "✅ operator[] on map creates default entry if key absent!",
            "✅ lower_bound/upper_bound for range queries on map/set",
            "❌ Don't use operator[] on map when you only want to read",
        ],
    },
    {
        title: "Container Adaptors", color: "#f0883e",
        items: [
            "✅ stack: DFS, balanced parentheses, undo stack",
            "✅ queue: BFS, task processing, producer-consumer",
            "✅ priority_queue: Dijkstra, K largest, scheduling",
            "✅ Max-heap is default — use greater<T> for min-heap",
            "✅ pop() returns void — always top() before pop()",
            "❌ Adaptors have no iterators — can't use STL algorithms",
            "❌ Never push to an empty and then pop without checking empty()",
        ],
    },
    {
        title: "Common Mistakes", color: "#f85149",
        items: [
            "❌ Invalidating iterators: vector insert/erase invalidates all",
            "❌ map operator[] inserts default value — use find() to check",
            "❌ multiset.erase(val) removes ALL copies — erase(iterator) for one",
            "❌ priority_queue is MAX heap by default — remember for min problems",
            "❌ list has no random access — don't use index-based for loop",
            "❌ unordered_map worst case O(n) — consider map for real-time code",
            "❌ stack/queue pop() returns void — always capture top()/front() first",
        ],
    },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MISSING DATA — listDequeTabs, adapterTabs alias,
//                containerComparison, utilTabs
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// adapterTabs is the correctly-spelled alias for adaptorTabs
const adapterTabs = adaptorTabs;

const listDequeTabs = [
    {
        label: "std::list",
        code: `#include <list>
#include <iostream>
#include <algorithm>

// ── std::list — doubly-linked list ────────────────────────────
// O(1) insert/erase anywhere WITH an iterator
// No random access — cannot use []
// Stable iterators — inserting/erasing doesn't invalidate others

std::list<int> lst = {3, 1, 4, 1, 5, 9, 2, 6};

// ── Add / Remove ──────────────────────────────────────────────
lst.push_front(0);        // {0, 3, 1, 4, 1, 5, 9, 2, 6}
lst.push_back(7);         // append to end
lst.pop_front();          // remove first
lst.pop_back();           // remove last

// ── Iterator-based insert / erase ────────────────────────────
auto it = std::find(lst.begin(), lst.end(), 4);
lst.insert(it, 99);       // insert 99 BEFORE 4 — O(1)!
lst.erase(it);            // erase element at it — O(1)!

// ── List-specific operations ──────────────────────────────────
lst.sort();               // O(n log n) in-place sort
lst.reverse();            // O(n) reverse in-place
lst.unique();             // remove consecutive duplicates (sort first!)
lst.remove(1);            // remove ALL elements equal to 1 — O(n)
lst.remove_if([](int x){ return x % 2 == 0; }); // remove all evens

// ── splice — O(1) move elements between lists ──────────────────
std::list<int> other = {10, 20, 30};
auto pos = lst.begin();
lst.splice(pos, other);           // move ALL of other into lst before pos
lst.splice(pos, other, other.begin()); // move single element

// ── Iterate ───────────────────────────────────────────────────
for (const auto& x : lst) std::cout << x << ' ';
// ❌ lst[2];  — NO random access! Use iterators.`,
    },
    {
        label: "std::deque",
        code: `#include <deque>
#include <iostream>
#include <algorithm>

// ── std::deque — double-ended queue ───────────────────────────
// O(1) push/pop at BOTH front AND back
// O(1) random access (slightly slower than vector — chunked storage)
// Invalidates all iterators on push_front/push_back (unlike vector)

std::deque<int> dq = {3, 1, 4, 1, 5};

// ── O(1) at BOTH ends ─────────────────────────────────────────
dq.push_front(0);         // {0, 3, 1, 4, 1, 5}
dq.push_back(9);          // {0, 3, 1, 4, 1, 5, 9}
dq.pop_front();           // remove first — O(1)! (vector: O(n))
dq.pop_back();            // remove last

// ── Random access ─────────────────────────────────────────────
dq[0];          // O(1) — like vector
dq.at(2);       // O(1) with bounds check
dq.front();     // first element
dq.back();      // last element

// ── Modify middle ─────────────────────────────────────────────
dq.insert(dq.begin() + 2, 99);  // insert at index 2 — O(n)
dq.erase(dq.begin());            // erase first — O(n)

// ── STL algorithms ────────────────────────────────────────────
std::sort(dq.begin(), dq.end());
std::reverse(dq.begin(), dq.end());

// ── Sliding window with deque (monotonic deque) ───────────────
// Classic interview pattern: max of each window of size k
std::vector<int> maxSliding(std::vector<int>& nums, int k) {
    std::deque<int> window;   // stores INDICES
    std::vector<int> result;
    for (int i = 0; i < (int)nums.size(); ++i) {
        while (!window.empty() && window.front() < i - k + 1)
            window.pop_front();  // outside window
        while (!window.empty() && nums[window.back()] <= nums[i])
            window.pop_back();   // smaller than current
        window.push_back(i);
        if (i >= k - 1) result.push_back(nums[window.front()]);
    }
    return result;
}`,
    },
    {
        label: "list vs deque vs vector",
        code: `// ── Choosing between list, deque, and vector ─────────────────
//
//                  vector    deque     list
//  Random access   O(1) ✅   O(1) ✅   ❌ none
//  push_back       O(1)*     O(1)      O(1)
//  push_front      O(n) ❌   O(1) ✅   O(1) ✅
//  Insert middle   O(n)      O(n)      O(1) ✅ (with iter)
//  Erase middle    O(n)      O(n)      O(1) ✅ (with iter)
//  Cache friendly  ✅ best   ⚠️ chunks  ❌ pointer hops
//  Iterator stable  ❌ push  ❌ push   ✅ always
//
//  * amortised — occasional reallocation

// ── Pick vector when: ──────────────────────────────────────────
//   • You need random access (arr[i])
//   • You mostly append to the back
//   • Cache performance matters (default choice)

// ── Pick deque when: ──────────────────────────────────────────
//   • You need O(1) push/pop at BOTH ends (sliding window, BFS queue)
//   • std::stack/queue adapter (deque is their default backing container)

// ── Pick list when: ───────────────────────────────────────────
//   • You have stable iterators and move elements between lists (splice)
//   • Frequent insert/erase in the MIDDLE with an existing iterator
//   • Elements are large and expensive to move

// ── Benchmark reality ─────────────────────────────────────────
// vector beats list even for O(n) insert in benchmarks on modern hardware
// because cache misses in linked-list pointer chasing dominate.
// Prefer vector unless profiling shows otherwise.`,
    },
];

const containerComparison = [
    { name: "vector<T>",          cat: "Sequence",   access: "O(1)",     insert: "O(1)* back / O(n) mid", erase: "O(n)",      find: "O(n)",     ordered: "❌", unique: "❌", note: "Default choice. Cache-friendly." },
    { name: "deque<T>",           cat: "Sequence",   access: "O(1)",     insert: "O(1) ends / O(n) mid",  erase: "O(n)",      find: "O(n)",     ordered: "❌", unique: "❌", note: "O(1) push_front. Slightly slower than vector." },
    { name: "list<T>",            cat: "Sequence",   access: "❌",       insert: "O(1) with iter",        erase: "O(1) iter", find: "O(n)",     ordered: "❌", unique: "❌", note: "O(1) splice. No random access." },
    { name: "stack<T>",           cat: "Adapters",   access: "top O(1)", insert: "O(1) push",             erase: "O(1) pop",  find: "❌",       ordered: "❌", unique: "❌", note: "LIFO. Wraps deque by default." },
    { name: "queue<T>",           cat: "Adapters",   access: "front O(1)",insert: "O(1) push",            erase: "O(1) pop",  find: "❌",       ordered: "❌", unique: "❌", note: "FIFO. Wraps deque by default." },
    { name: "priority_queue<T>",  cat: "Adapters",   access: "top O(1)", insert: "O(log n)",              erase: "O(log n)",  find: "❌",       ordered: "✅ heap", unique: "❌", note: "Max-heap. Use greater<T> for min-heap." },
    { name: "set<T>",             cat: "Ordered",    access: "O(log n)", insert: "O(log n)",              erase: "O(log n)",  find: "O(log n)", ordered: "✅", unique: "✅", note: "Red-Black tree. Sorted. Range queries." },
    { name: "multiset<T>",        cat: "Ordered",    access: "O(log n)", insert: "O(log n)",              erase: "O(log n)",  find: "O(log n)", ordered: "✅", unique: "❌", note: "Like set but allows duplicates." },
    { name: "map<K,V>",           cat: "Ordered",    access: "O(log n)", insert: "O(log n)",              erase: "O(log n)",  find: "O(log n)", ordered: "✅", unique: "✅", note: "Sorted by key. [] creates missing keys!" },
    { name: "multimap<K,V>",      cat: "Ordered",    access: "O(log n)", insert: "O(log n)",              erase: "O(log n)",  find: "O(log n)", ordered: "✅", unique: "❌", note: "One key → many values. Use equal_range." },
    { name: "unordered_set<T>",   cat: "Unordered",  access: "O(1) avg", insert: "O(1) avg",              erase: "O(1) avg",  find: "O(1) avg", ordered: "❌", unique: "✅", note: "Hash table. No ordering guarantee." },
    { name: "unordered_map<K,V>", cat: "Unordered",  access: "O(1) avg", insert: "O(1) avg",              erase: "O(1) avg",  find: "O(1) avg", ordered: "❌", unique: "✅", note: "Fastest lookup for large n. reserve()!" },
];

const utilTabs = [
    {
        label: "pair & tuple",
        code: `#include <utility>    // pair, make_pair
#include <tuple>       // tuple, make_tuple, get
#include <iostream>

// ── std::pair<A,B> — two values of possibly different types ───
std::pair<std::string, int> person = {"Alice", 30};
person.first;     // "Alice"
person.second;    // 30

auto p2 = std::make_pair("Bob", 25);     // type deduced

// ── C++17 structured binding ──────────────────────────────────
auto [name, age] = person;               // unpack pair
std::cout << name << " is " << age << "\\n";

// ── pair from map iterator ─────────────────────────────────────
std::map<std::string, int> m = {{"x",1},{"y",2}};
for (const auto& [key, val] : m)         // structured binding on map
    std::cout << key << "=" << val << ' ';

// ── std::tuple<A,B,C...> — N values of mixed types ────────────
auto t = std::make_tuple(42, 3.14, std::string("hello"), true);

// Access by index (compile-time):
std::get<0>(t);   // 42
std::get<1>(t);   // 3.14
std::get<2>(t);   // "hello"

// ── C++17 structured binding for tuple ───────────────────────
auto [n, pi, msg, flag] = t;
std::cout << n << ' ' << pi << ' ' << msg << "\\n";

// ── tie — unpack into existing variables ─────────────────────
int a; double b; std::string c;
std::tie(a, b, c, std::ignore) = t;     // std::ignore skips bool`,
    },
    {
        label: "std::optional",
        code: `#include <optional>
#include <string>
#include <iostream>
#include <vector>

// ── std::optional<T> — a value OR nothing ─────────────────────
// Replaces: -1 sentinel, nullptr, bool out-param, pointer-as-optional

// ── Return optional from search function ─────────────────────
std::optional<int> findIndex(const std::vector<int>& v, int target) {
    for (int i = 0; i < (int)v.size(); ++i)
        if (v[i] == target) return i;          // return the value
    return std::nullopt;                        // return "nothing"
}

// ── Check and use ─────────────────────────────────────────────
auto idx = findIndex({1,2,3,4,5}, 3);

if (idx) {                                     // bool conversion
    std::cout << "Found at: " << *idx << "\\n"; // * to get value
}
if (idx.has_value())                           // explicit check
    std::cout << idx.value() << "\\n";

// ── value_or — provide default if absent ─────────────────────
int pos = idx.value_or(-1);   // -1 if not found

// ── Chain transformations (C++23 transform, but do manually) ──
std::optional<std::string> maybeName;
// Safe access:
if (maybeName) {
    std::cout << maybeName->size() << "\\n";   // -> dereferences
}

// ── Creating optionals ────────────────────────────────────────
std::optional<int> a = 42;             // has value
std::optional<int> b;                  // empty (nullopt)
std::optional<int> c = std::nullopt;   // explicitly empty
auto d = std::make_optional(99);       // like make_shared`,
    },
    {
        label: "std::variant",
        code: `#include <variant>
#include <string>
#include <iostream>

// ── std::variant<A,B,...> — type-safe union ────────────────────
// Holds exactly ONE of the listed types at a time
// Size = max(sizeof all types) + tag byte

using IntOrStr = std::variant<int, std::string>;

IntOrStr v = 42;               // holds int
v = std::string("hello");      // now holds string

// ── Check which type is active ────────────────────────────────
std::holds_alternative<int>(v);         // false — it's string now
std::holds_alternative<std::string>(v); // true

// ── Access the value ──────────────────────────────────────────
std::get<std::string>(v);               // "hello" — throws if wrong type
std::get<1>(v);                         // by index — std::string is index 1

auto* ptr = std::get_if<std::string>(&v);  // nullptr if wrong type — safe
if (ptr) std::cout << *ptr;

// ── std::visit — pattern matching ────────────────────────────
std::visit([](const auto& val) {        // called with the active type
    std::cout << val << "\\n";
}, v);

// ── Overloaded visitor (C++17) ────────────────────────────────
struct Visitor {
    void operator()(int n)               { std::cout << "int: " << n; }
    void operator()(const std::string& s){ std::cout << "str: " << s; }
};
std::visit(Visitor{}, v);

// ── Error handling with variant ────────────────────────────────
using Result = std::variant<double, std::string>;   // value or error msg
Result safeDivide(double a, double b) {
    if (b == 0) return std::string("division by zero");
    return a / b;
}`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export default function CppCollections() {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [vecTab,     setVecTab]     = useState(0);
    const [ldTab,      setLdTab]      = useState(0);
    const [adpTab,     setAdpTab]     = useState(0);
    const [setTab,     setSetTab]     = useState(0);
    const [mapTab,     setMapTab]     = useState(0);
    const [utilTab,    setUtilTab]    = useState(0);
    const [cmpFilter,  setCmpFilter]  = useState("All");

    const cmpCategories = ["All", "Sequence", "Adapters", "Ordered", "Unordered"];
    const containerGroups = {
        Sequence:   ["vector<T>","deque<T>","list<T>"],
        Adapters:   ["stack<T>","queue<T>","priority_queue<T>"],
        Ordered:    ["set<T>","multiset<T>","map<K,V>","multimap<K,V>"],
        Unordered:  ["unordered_set<T>","unordered_map<K,V>"],
    };
    const visibleRows = cmpFilter === "All"
        ? containerComparison
        : containerComparison.filter(r =>
            (containerGroups[cmpFilter] || []).some(n => r.name.startsWith(n.split("<")[0]))
        );

    return (
        <PageWrapper>

            {/* ── Page Header ── */}
            <Box sx={{ textAlign: "center", mb: 5, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{
                    fontWeight: 800, color: "goldenrod", display: "inline-block",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    boxShadow: "0 6px 20px rgba(218,165,32,0.3)",
                    border: "1px solid rgba(218,165,32,0.25)", letterSpacing: "0.04em",
                }}>
                    C++ COLLECTIONS
                </Typography>
                <Typography sx={{ mt: 2, color: "rgba(255,255,255,0.45)", fontSize: "0.88rem" }}>
                    STL Containers — vector · list · deque · stack · queue · set · map · pair · optional · variant
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mt: 2 }}>
                    {["vector","list","deque","stack","queue","priority_queue","set","map","unordered_map","pair","optional","variant"].map(t => (
                        <Chip key={t} label={t} size="small" sx={{
                            bgcolor: "rgba(218,165,32,0.1)", color: "rgba(218,165,32,0.85)",
                            border: "1px solid rgba(218,165,32,0.25)", fontSize: "0.72rem",
                        }} />
                    ))}
                </Box>
            </Box>

            {/* ── Intro Banner ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 4 }}>
                <Box sx={{ bgcolor: "rgba(218,165,32,0.06)", border: "1px solid rgba(218,165,32,0.2)", borderRadius: "14px", p: "18px 24px", display: "flex", gap: 2, alignItems: "flex-start" }}>
                    <Typography sx={{ fontSize: "1.4rem", flexShrink: 0 }}>💡</Typography>
                    <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.85, color: "rgba(255,255,255,0.8)" }}>
                        The C++ STL provides <strong style={{ color: "goldenrod" }}>12+ container types</strong> across
                        three categories: <strong style={{ color: "goldenrod" }}>sequence containers</strong> (vector, list, deque),{" "}
                        <strong style={{ color: "goldenrod" }}>associative containers</strong> (set, map, and their unordered variants),
                        and <strong style={{ color: "goldenrod" }}>container adapters</strong> (stack, queue, priority_queue).
                        Choosing the right container is one of the most impactful decisions in a C++ program.
                    </Typography>
                </Box>
            </Box>

            {/* ── Category overview ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 4 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 2 }}>
                    {[
                        { cat: "Sequence Containers", icon: "📋", color: "#f0883e", desc: "Maintain insertion order. Elements stored and accessed by position.", items: ["vector — contiguous, random access", "deque — fast both-end ops", "list — O(1) insert/erase anywhere"] },
                        { cat: "Associative Containers", icon: "🔑", color: "#58a6ff", desc: "Keys/values kept sorted (ordered) or in hash buckets (unordered).", items: ["set/multiset — sorted unique/dup keys", "map/multimap — key→value sorted", "unordered_* — O(1) hash-based"] },
                        { cat: "Container Adapters", icon: "🔧", color: "#7ee787", desc: "Constrained interfaces built on top of sequence containers.", items: ["stack — LIFO (push/pop/top)", "queue — FIFO (push/pop/front)", "priority_queue — largest first"] },
                    ].map((c, i) => (
                        <Box key={i} sx={{ bgcolor: "rgba(26,14,52,0.9)", border: `1px solid ${c.color}44`, borderTop: `3px solid ${c.color}`, borderRadius: "14px", p: "16px 18px" }}>
                            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 1 }}>
                                <Typography sx={{ fontSize: "1.4rem" }}>{c.icon}</Typography>
                                <Typography sx={{ fontWeight: 800, color: c.color, fontSize: "0.88rem" }}>{c.cat}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: "0.8rem", opacity: 0.65, mb: 1, lineHeight: 1.55 }}>{c.desc}</Typography>
                            {c.items.map((it, j) => (
                                <Typography key={j} sx={{ fontFamily: "monospace", fontSize: "0.76rem", color: c.color, opacity: 0.85, lineHeight: 1.7 }}>• {it}</Typography>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>

                    {/* ════════════════════════════════════════════════════
              SECTION 1 — std::vector
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            1. std::vector — Dynamic Array
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            <code style={{ color: "goldenrod" }}>std::vector</code> is the <strong>default container</strong> in
                            C++. It stores elements in contiguous heap memory, supports O(1) random access and
                            amortised O(1) push_back. Use it unless you have a specific reason for another container.
                        </Typography>

                        <ConceptCard title="Contiguous Memory = Cache Friendly" color="#f0883e">
                            <CodeBlock>{`// vector stores all elements adjacent in memory:
//   addr:  1000 1004 1008 1012 1016
//   value: [10] [20] [30] [40] [50]
//
// CPU cache loads a cache line (~64 bytes = 16 ints) at once.
// Iterating a vector hits cache almost every access — very fast!
//
// list stores each element separately on the heap:
//   [10]→[ptr]  scattered  [20]→[ptr]  scattered  [30]→[ptr]
//
// Each access may miss the cache line → 100x slower than vector!
// This is why vector beats list even for O(n) insert/erase in practice.`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={vecTab} onChange={(_, v) => setVecTab(v)} tabs={vectorTabs.map(t => t.label)} />
                        <CodeBlock>{vectorTabs[vecTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 2 — list & deque
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            2. std::list &amp; std::deque
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            <code style={{ color: "goldenrod" }}>std::list</code> is a doubly-linked list offering
                            O(1) insert/erase anywhere with stable iterators.{" "}
                            <code style={{ color: "goldenrod" }}>std::deque</code> is a double-ended queue with O(1)
                            push/pop at <em>both</em> ends and random access — bridging list and vector.
                        </Typography>

                        <StyledTabs value={ldTab} onChange={(_, v) => setLdTab(v)} tabs={listDequeTabs.map(t => t.label)} />
                        <CodeBlock>{listDequeTabs[ldTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 3 — CONTAINER ADAPTERS
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            3. Container Adapters — stack, queue, priority_queue
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            Adapters wrap a sequence container and restrict its interface to enforce a
                            specific access pattern. They provide semantic clarity and prevent accidental
                            misuse — you can't iterate a stack or random-access a queue.
                        </Typography>

                        <ConceptCard title="LIFO vs FIFO vs Priority" color="#7ee787">
                            <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 2 }}>
                                {[
                                    { name: "stack (LIFO)", color: "#f0883e", visual: "push→ [5][4][3][2][1] ←top\n                pop ←", ops: "push, pop, top, empty, size" },
                                    { name: "queue (FIFO)", color: "#58a6ff", visual: "push→ [1][2][3][4][5] ←front\n          back→        pop ←", ops: "push, pop, front, back, empty, size" },
                                    { name: "priority_queue",color:"#7ee787", visual: "push(5,2,8,1)→ heap\ntop()=8 (max on top)", ops: "push, pop, top, empty, size" },
                                ].map((a, i) => (
                                    <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.03)", border: `1px solid ${a.color}33`, borderRadius: "10px", p: "12px 14px" }}>
                                        <Typography sx={{ fontWeight: 700, color: a.color, fontSize: "0.82rem", mb: 0.75 }}>{a.name}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", fontSize: "0.73rem", color: "rgba(255,255,255,0.45)", mb: 0.75, whiteSpace: "pre" }}>{a.visual}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", fontSize: "0.73rem", color: a.color, opacity: 0.8 }}>{a.ops}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </ConceptCard>

                        <StyledTabs value={adpTab} onChange={(_, v) => setAdpTab(v)} tabs={adapterTabs.map(t => t.label)} />
                        <CodeBlock>{adapterTabs[adpTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 4 — set family
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            4. set, multiset &amp; unordered_set
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            The <strong>set family</strong> provides membership testing and uniqueness enforcement.
                            Ordered variants (<code style={{ color: "goldenrod" }}>std::set</code>) use a
                            Red-Black Tree and support range queries. Unordered variants use a hash table
                            for O(1) average lookups at the cost of ordering.
                        </Typography>

                        <StyledTabs value={setTab} onChange={(_, v) => setSetTab(v)} tabs={setTabs.map(t => t.label)} />
                        <CodeBlock>{setTabs[setTab].code}</CodeBlock>

                        {/* set methods */}
                        <SectionHeading icon={<AiFillBulb size={18} />} title="Set / Map Common Methods" subtitle="Applies to set, multiset, map, multimap — O(log n) ordered, O(1) unordered" />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {[
                                { m: "insert(val) / emplace(args...)", color: "#56d364",  desc: "Insert element. insert returns pair<iterator,bool>. emplace constructs in-place." },
                                { m: "find(key)",                      color: "#58a6ff",  desc: "Return iterator to element, or end() if not found. O(log n) ordered, O(1) unordered." },
                                { m: "count(key)",                     color: "#fbbf24",  desc: "Number of matching elements. Always 0 or 1 for set/map; >=0 for multiset/multimap." },
                                { m: "contains(key)",                  color: "#7ee787",  desc: "C++20: returns bool. Cleaner than count() > 0 or find() != end()." },
                                { m: "erase(key / iterator)",          color: "#f85149",  desc: "Erase by value removes ALL matches (multiset/multimap). Erase by iterator removes one." },
                                { m: "lower_bound(key) / upper_bound", color: "#a855f7",  desc: "Return iterator to first >= or first > key. Ordered containers only!" },
                                { m: "equal_range(key)",               color: "#e879f9",  desc: "Returns {lower_bound, upper_bound} pair — all elements matching key." },
                            ].map((row, i) => (
                                <Box key={i} sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.5fr 2fr", gap: "12px", bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${row.color}22`, borderLeft: `3px solid ${row.color}`, borderRadius: "12px", px: 2, py: "10px", alignItems: "center" }}>
                                    <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: row.color, fontSize: "0.8rem" }}>{row.m}</Typography>
                                    <Typography sx={{ fontSize: "0.8rem", opacity: 0.78, lineHeight: 1.6 }}>{row.desc}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 5 — map family
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            5. map, multimap &amp; unordered_map
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            The <strong>map family</strong> stores key-value pairs. Ordered maps keep keys sorted
                            and support range queries; unordered maps use hashing for O(1) average access.
                            The critical pitfall: <code style={{ color: "goldenrod" }}>operator[]</code> creates
                            a default entry for missing keys — always use <code style={{ color: "goldenrod" }}>find()</code> to check existence.
                        </Typography>

                        <ConceptCard title="⚠️ operator[] Danger Zone" color="#f85149">
                            <CodeBlock>{`std::map<std::string, int> scores;
scores["Alice"] = 95;

// ── DANGER: operator[] creates key if absent ──────────────────
int s = scores["Zara"];   // ← creates "Zara" with value 0!
scores.size();             // 2 — Zara sneaked in!

// ── SAFE alternatives ─────────────────────────────────────────
if (scores.count("Zara"))          // 1. check with count
    use(scores["Zara"]);

auto it = scores.find("Zara");     // 2. find returns iterator
if (it != scores.end())
    use(it->second);

if (scores.contains("Zara"))       // 3. C++20 contains()
    use(scores.at("Zara"));        // at() throws if missing

// ── When operator[] IS safe ───────────────────────────────────
scores["Bob"]++;           // intentional: 0+1=1 (default init is fine)
wordCount[word]++;         // word frequency — missing key starts at 0`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={mapTab} onChange={(_, v) => setMapTab(v)} tabs={mapTabs.map(t => t.label)} />
                        <CodeBlock>{mapTabs[mapTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 6 — pair, tuple, optional, variant
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            6. Utility Types — pair, tuple, optional, variant
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            These types let you bundle multiple values together or represent values that
                            might be absent. They eliminate the need for out-parameters, sentinel values
                            like <code style={{ color: "goldenrod" }}>-1</code>, and raw unions.
                        </Typography>

                        <StyledTabs value={utilTab} onChange={(_, v) => setUtilTab(v)} tabs={utilTabs.map(t => t.label)} />
                        <CodeBlock>{utilTabs[utilTab].code}</CodeBlock>

                        {/* Utility types summary */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🧰</span>} title="Utility Types Quick Reference" />
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2,1fr)", gap: 2 }}>
                            {[
                                { name: "std::pair<A,B>",      color: "#f0883e", hdr: "<utility>",   desc: "Two values, possibly different types. Access via .first / .second. Used by map iterators.", ex: 'auto p = make_pair("hi", 42);' },
                                { name: "std::tuple<A,B,C...>",color: "#58a6ff", hdr: "<tuple>",     desc: "Fixed collection of N values of mixed types. Access via std::get<I>(t).", ex: 'auto t = make_tuple(1, 3.14, "x");' },
                                { name: "std::optional<T>",    color: "#7ee787", hdr: "<optional>",  desc: "A value or nothing. Replaces sentinel values (-1, nullptr). Check with if(opt) or has_value().", ex: 'optional<int> findIdx(...)' },
                                { name: "std::variant<A,B...>",color: "#a855f7", hdr: "<variant>",   desc: "Type-safe union — exactly one active type. Pattern-match with std::visit.", ex: 'variant<int,string> v = 42;' },
                            ].map((item, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${item.color}33`, borderTop: `3px solid ${item.color}`, borderRadius: "12px", p: "14px 16px" }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.75 }}>
                                        <Typography sx={{ fontFamily: "monospace", fontWeight: 800, color: item.color, fontSize: "0.85rem" }}>{item.name}</Typography>
                                        <PillChip label={item.hdr} color={item.color} />
                                    </Box>
                                    <Typography sx={{ fontSize: "0.8rem", opacity: 0.72, mb: 0.75, lineHeight: 1.6 }}>{item.desc}</Typography>
                                    <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.77rem" }}>{item.ex}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 7 — FULL COMPARISON TABLE
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={3}>
                            Container Comparison &amp; Cheatsheet
                        </Typography>

                        {/* Comparison table with filter */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>📊</span>} title="All Containers Side by Side" />
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                            {cmpCategories.map(cat => (
                                <Chip key={cat} label={cat} onClick={() => setCmpFilter(cat)} variant={cmpFilter === cat ? "filled" : "outlined"} size="small"
                                      sx={{ cursor: "pointer", fontSize: "0.72rem", fontWeight: 600, color: cmpFilter === cat ? "#100720" : "rgba(255,255,255,0.55)", bgcolor: cmpFilter === cat ? "goldenrod" : "transparent", borderColor: cmpFilter === cat ? "goldenrod" : "rgba(255,255,255,0.2)", "&:hover": { borderColor: "goldenrod", color: "goldenrod" } }} />
                            ))}
                        </Box>
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)", mb: 4 }}>
                            {!isMobile && <TableHeader columns={["Container","Ordering","Unique?","Access","Insert","Lookup","Best For"]} cols="1.4fr 90px 80px 100px 100px 100px 1.5fr" />}
                            {visibleRows.map((row, i) => {
                                const colors = { "vector<T>":"#f0883e","deque<T>":"#f0883e","list<T>":"#f0883e","stack<T>":"#7ee787","queue<T>":"#7ee787","priority_queue<T>":"#7ee787","set<T>":"#58a6ff","multiset<T>":"#58a6ff","unordered_set<T>":"#a855f7","map<K,V>":"#58a6ff","multimap<K,V>":"#58a6ff","unordered_map<K,V>":"#a855f7" };
                                const c = colors[row.name] || "goldenrod";
                                return isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: c, fontSize: "0.85rem", mb: 0.5 }}>{row.name}</Typography>
                                        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.5)", mb: 0.4 }}>{row.order} · Access: {row.access}</Typography>
                                        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.5)", mb: 0.4 }}>Insert: {row.insert} · Lookup: {row.lookup}</Typography>
                                        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.75)" }}>{row.use}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1.4fr 90px 80px 100px 100px 100px 1.5fr", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", fontSize: "0.82rem", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontFamily: "monospace", fontWeight: 700, color: c, fontSize: "0.8rem" }}>{row.name}</Box>
                                        <Box sx={{ fontSize: "0.77rem", opacity: 0.7 }}>{row.order}</Box>
                                        <Box sx={{ fontSize: "0.77rem" }}>{row.unique}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#fbbf24", fontSize: "0.76rem" }}>{row.access}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#56d364", fontSize: "0.76rem" }}>{row.insert}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#58a6ff", fontSize: "0.76rem" }}>{row.lookup}</Box>
                                        <Box sx={{ fontSize: "0.78rem", opacity: 0.75 }}>{row.use}</Box>
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Cheat blocks */}
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 3 }}>
                            {cheatBlocks.map((block, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${block.color}33`, borderRadius: "14px", p: "18px 20px" }}>
                                    <Typography sx={{ fontWeight: 800, color: block.color, fontSize: "0.88rem", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>{block.title}</Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.65 }}>
                                        {block.items.map((item, j) => (
                                            <Typography key={j} sx={{ fontSize: "0.81rem", opacity: 0.82, lineHeight: 1.6 }}>{item}</Typography>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                </Grid>
            </Grid>
        </PageWrapper>
    );
}
